import React, { useState, Suspense, useEffect, useRef } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, Box, Text, Plane } from '@react-three/drei';
import { Paper, Box as MuiBox, Typography, Button, Alert, Slider, Chip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import * as THREE from 'three';

// Auto-camera that frames the entire model in isometric view
function AutoCamera({ scene }) {
  const { camera, controls } = useThree();

  useEffect(() => {
    if (!scene) return;

    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Calculate distance to fit the entire model
    const fov = 50;
    const distance = (maxDim / (2 * Math.tan((fov * Math.PI / 180) / 2))) * 2.0;
    const isoDistance = Math.max(distance, maxDim * 1.5);

    // Isometric angle
    camera.position.set(
      center.x + isoDistance,
      center.y + isoDistance * 0.8,
      center.z + isoDistance
    );

    // Update near/far planes to accommodate model size
    camera.near = isoDistance * 0.001;
    camera.far = isoDistance * 10;
    camera.updateProjectionMatrix();

    camera.lookAt(center);

    if (controls) {
      controls.target.copy(center);
      controls.minDistance = isoDistance * 0.05;
      controls.maxDistance = isoDistance * 5;
      controls.update();
    }
  }, [scene, camera, controls]);

  return null;
}

// Ground plane that receives shadows
function GroundPlane() {
  return (
    <Plane
      args={[50, 50]}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.01, 0]}
      receiveShadow
    >
      <meshStandardMaterial color="#e0e0e0" roughness={0.8} metalness={0.1} />
    </Plane>
  );
}

// Model component with shadow casting and optional rotation (degrees)
function Model({ url, onError, onLoad, rotation }) {
  const { scene } = useGLTF(url);

  const rotRad = {
    x: ((rotation?.x || 0) * Math.PI) / 180,
    y: ((rotation?.y || 0) * Math.PI) / 180,
    z: ((rotation?.z || 0) * Math.PI) / 180,
  };

  useEffect(() => {
    if (scene) {
      scene.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          if (child.material) {
            child.material.roughness = 0.6;
            child.material.metalness = 0.1;
          }
        }
      });
      onLoad?.(scene);
    }
  }, [scene, onLoad]);

  return <primitive object={scene} scale={1} rotation={[rotRad.x, rotRad.y, rotRad.z]} />;
}

// Placeholder model when no file exists
function PlaceholderModel() {
  return (
    <>
      <Box args={[3, 2, 0.2]} position={[0, 0, 0]} castShadow receiveShadow>
        <meshStandardMaterial color="#8B4513" roughness={0.6} />
      </Box>
      <Box args={[0.1, 2, 2]} position={[-1.5, 0, 1]} castShadow receiveShadow>
        <meshStandardMaterial color="#654321" roughness={0.6} />
      </Box>
      <Box args={[0.1, 2, 2]} position={[1.5, 0, 1]} castShadow receiveShadow>
        <meshStandardMaterial color="#654321" roughness={0.6} />
      </Box>
      <Box args={[3, 0.1, 2]} position={[0, -1, 1]} castShadow receiveShadow>
        <meshStandardMaterial color="#654321" roughness={0.6} />
      </Box>
      <Box args={[3, 0.1, 2]} position={[0, 1, 1]} castShadow receiveShadow>
        <meshStandardMaterial color="#654321" roughness={0.6} />
      </Box>
      <Text
        position={[0, 2.5, 0]}
        fontSize={0.3}
        color="#333"
        anchorX="center"
      >
        Sample Woodworking Model
      </Text>
    </>
  );
}

// Main CAD-style viewer with full shadow support
function Model3DViewerCAD({ modelUrl, modelRotation, modelDebug }) {
  const [autoRotate, setAutoRotate] = useState(false);
  const [useSample, setUseSample] = useState(!modelUrl);
  const [loadError, setLoadError] = useState(null);
  const [loadedScene, setLoadedScene] = useState(null);

  // DEBUG: live rotation override
  const [debugRot, setDebugRot] = useState({
    x: modelRotation?.x || 0,
    y: modelRotation?.y || 0,
    z: modelRotation?.z || 0,
  });
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const json = `"modelRotation": { "x": ${debugRot.x}, "y": ${debugRot.y}, "z": ${debugRot.z} }`;
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleZoomOut = () => {
    setLoadedScene(null);
    setTimeout(() => setLoadedScene((prev) => prev), 100);
  };

  const toggleAutoRotate = () => {
    setAutoRotate((prev) => !prev);
  };

  const handleModelError = (error) => {
    console.error('Model loading failed:', error);
    setLoadError('Failed to load 3D model. File may be missing or corrupted.');
    setUseSample(true);
  };

  const handleModelLoad = (scene) => {
    setLoadedScene(scene);
  };

  const displayUrl = useSample ? null : modelUrl;

  return (
    <Paper elevation={2} sx={{ overflow: 'hidden' }}>
      {loadError && (
        <Alert severity="warning" sx={{ m: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}
      {useSample && !loadError && (
        <Alert severity="info" sx={{ m: 2 }}>
          Showing sample model. Upload a 3D model (.gltf or .glb) to view your actual project.
        </Alert>
      )}

      <MuiBox sx={{ position: 'relative', height: '500px', bgcolor: '#f5f5f5' }}>
        <Canvas
          shadows="basic"
          camera={{ position: [5, 5, 5], fov: 50 }}
          gl={{ antialias: true, powerPreference: 'default' }}
          dpr={Math.min(window.devicePixelRatio, 1.5)}
        >
          {/* Ambient light for base illumination */}
          <ambientLight intensity={0.5} />

          {/* Single directional light with modest shadow map */}
          <directionalLight
            position={[10, 20, 10]}
            intensity={1.0}
            castShadow
            shadow-mapSize-width={512}
            shadow-mapSize-height={512}
            shadow-camera-far={200}
            shadow-camera-left={-30}
            shadow-camera-right={30}
            shadow-camera-top={30}
            shadow-camera-bottom={-30}
            shadow-bias={-0.001}
          />

          {/* Fill light - no shadows */}
          <directionalLight
            position={[-10, 10, -10]}
            intensity={0.3}
          />

          <Environment preset="warehouse" />

          <Suspense fallback={null}>
            {displayUrl ? (
              <Model url={displayUrl} onError={handleModelError} onLoad={handleModelLoad} rotation={debugRot} />
            ) : (
              <PlaceholderModel />
            )}
          </Suspense>

          {/* Ground plane for shadows */}
          <GroundPlane />

          {loadedScene && <AutoCamera scene={loadedScene} />}

          <OrbitControls
            autoRotate={autoRotate}
            autoRotateSpeed={2}
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minDistance={0.1}
            maxDistance={10000}
          />
        </Canvas>

        <MuiBox
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            p: 1,
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ZoomOutIcon />}
            onClick={handleZoomOut}
          >
            Reset View
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RotateRightIcon />}
            onClick={toggleAutoRotate}
          >
            {autoRotate ? 'Stop' : 'Rotate'}
          </Button>
        </MuiBox>

        {/* DEBUG rotation panel - only shown when modelDebug is true in projects.json */}
        {modelDebug && <MuiBox
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            bgcolor: 'rgba(0,0,0,0.75)',
            color: '#fff',
            p: 2,
            borderRadius: 2,
            width: 220,
            boxShadow: 3,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
            � DEBUG: Model Rotation
          </Typography>

          {['x', 'y', 'z'].map((axis) => (
            <MuiBox key={axis} sx={{ mb: 1 }}>
              <Typography variant="caption">
                {axis.toUpperCase()}: {debugRot[axis]}°
              </Typography>
              <Slider
                size="small"
                min={-180}
                max={180}
                step={5}
                value={debugRot[axis]}
                onChange={(_, val) => setDebugRot((prev) => ({ ...prev, [axis]: val }))}
                sx={{ color: '#90caf9', py: 0.5 }}
              />
            </MuiBox>
          ))}

          <Button
            size="small"
            variant="contained"
            fullWidth
            onClick={handleCopy}
            sx={{ mt: 0.5, fontSize: '0.7rem' }}
          >
            {copied ? '✅ Copied!' : '📋 Copy JSON'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7, wordBreak: 'break-all' }}>
            x:{debugRot.x} y:{debugRot.y} z:{debugRot.z}
          </Typography>
        </MuiBox>}

        <MuiBox
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255, 255, 255, 0.85)',
            p: 1,
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Typography variant="caption" display="block">🖱️ Drag to rotate</Typography>
          <Typography variant="caption" display="block">🖱️ Right-drag to pan</Typography>
          <Typography variant="caption" display="block">🖱️ Scroll to zoom</Typography>
        </MuiBox>
      </MuiBox>
    </Paper>
  );
}

export default Model3DViewerCAD;
